import {udExternalLoaders} from '@udemy/shared-utils';

declare global {
    interface Window {
        google?: {
            accounts?: {
                oauth2?: {
                    initTokenClient: (config: {
                        client_id: string;
                        scope: string;
                        callback: string;
                        error_callback: string;
                        ux_mode: string;
                    }) => TokenClient;
                };
            };
        };
    }
}

export interface GoogleTokenResponse {
    access_token: string;
    expires_in: number;
    error?: string;
}

export interface TokenClient {
    callback: string | ((response: GoogleTokenResponse) => void);
    error_callback: string | ((error: {type?: string}) => void);
    requestAccessToken: () => void;
}

const CALENDAR_EVENTS_SCOPE = 'https://www.googleapis.com/auth/calendar.events';

/**
 * Loads the Google Identity Services script via the shared external-script loader
 * (respects the brand's `is_external_sources_enabled` third-party-consent gate) rather
 * than injecting the <script> tag by hand.
 */
export function loadGoogleIdentityScript(): Promise<NonNullable<Window['google']>> {
    return new Promise((resolve) => {
        udExternalLoaders.loadGoogleAuth((google) => {
            resolve(google as NonNullable<Window['google']>);
        });
    });
}

export async function initGoogleTokenClient(clientId: string): Promise<TokenClient | null> {
    const google = window.google?.accounts?.oauth2
        ? window.google
        : await loadGoogleIdentityScript();

    return (
        google?.accounts?.oauth2?.initTokenClient({
            client_id: clientId,
            scope: CALENDAR_EVENTS_SCOPE,
            callback: '',
            error_callback: '',
            ux_mode: 'popup',
        }) ?? null
    );
}

export function requestGoogleAccessToken(
    tokenClient: TokenClient | null,
): Promise<GoogleTokenResponse> {
    return new Promise((resolve, reject) => {
        if (!tokenClient) {
            reject(new Error('not_loaded'));
            return;
        }
        tokenClient.callback = (response: GoogleTokenResponse) => {
            if (response.error) {
                reject(new Error(response.error));
            } else {
                resolve(response);
            }
        };
        tokenClient.error_callback = (error: {type?: string}) => {
            reject(new Error(error.type ?? 'google_auth_error'));
        };
        tokenClient.requestAccessToken();
    });
}
